import { Badge as BadgeType } from '@/types/gamification';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Lock } from 'lucide-react';

interface BadgeDisplayProps {
  badge: BadgeType;
  size?: 'sm' | 'md' | 'lg';
  onClick?: () => void;
}

export const BadgeDisplay = ({ badge, size = 'md', onClick }: BadgeDisplayProps) => {
  const sizeClasses = {
    sm: 'w-16 h-16 text-2xl',
    md: 'w-24 h-24 text-4xl',
    lg: 'w-32 h-32 text-5xl'
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const getCategoryStyles = () => {
    // Always show category color (even when locked) using design tokens (HSL)
    switch (badge.category) {
      case 'learning':
        return {
          card: 'border-[hsl(var(--primary))] bg-gradient-to-br from-[hsl(var(--primary)/0.22)] via-[hsl(var(--primary)/0.12)] to-[hsl(var(--accent)/0.22)]',
          icon: 'bg-gradient-to-br from-[hsl(var(--primary)/0.35)] to-[hsl(var(--accent)/0.35)]'
        };
      case 'streak':
        return {
          card: 'border-[hsl(var(--accent))] bg-gradient-to-br from-[hsl(var(--accent)/0.22)] via-[hsl(var(--accent)/0.12)] to-[hsl(var(--primary)/0.22)]',
          icon: 'bg-gradient-to-br from-[hsl(var(--accent)/0.35)] to-[hsl(var(--primary)/0.35)]'
        };
      case 'completion':
        return {
          card: 'border-[hsl(var(--secondary))] bg-gradient-to-br from-[hsl(var(--secondary)/0.22)] via-[hsl(var(--secondary)/0.12)] to-[hsl(var(--primary)/0.22)]',
          icon: 'bg-gradient-to-br from-[hsl(var(--secondary)/0.35)] to-[hsl(var(--primary)/0.35)]'
        };
      case 'quiz':
        return {
          card: 'border-[hsl(var(--accent))] bg-gradient-to-br from-[hsl(var(--accent)/0.22)] via-[hsl(var(--accent)/0.12)] to-[hsl(var(--secondary)/0.22)]',
          icon: 'bg-gradient-to-br from-[hsl(var(--accent)/0.35)] to-[hsl(var(--secondary)/0.35)]'
        };
      default:
        return {
          card: 'border-[hsl(var(--primary))] bg-gradient-to-br from-[hsl(var(--primary)/0.2)] to-[hsl(var(--secondary)/0.2)]',
          icon: 'bg-gradient-to-br from-[hsl(var(--primary)/0.3)] to-[hsl(var(--secondary)/0.3)]'
        };
    }
  };
  const styles = getCategoryStyles();

  return (
    <Card 
      className={`${styles.card} transition-all hover:scale-105 border-2 ${onClick && badge.unlocked ? 'cursor-pointer hover:shadow-lg' : ''}`}
      onClick={onClick && badge.unlocked ? onClick : undefined}
    >
      <CardContent className="p-4 flex flex-col items-center text-center space-y-2">
        <div className={`${sizeClasses[size]} rounded-full ${styles.icon} flex items-center justify-center relative shadow-md`}>
          {badge.unlocked ? (
            <span className="animate-scale-in drop-shadow-sm">{badge.icon}</span>
          ) : (
            <Lock className="w-1/2 h-1/2 text-muted-foreground" />
          )}
        </div>
        <div>
          <h4 className={`font-semibold ${textSizes[size]}`}>{badge.name}</h4>
          <p className={`text-muted-foreground ${size === 'sm' ? 'text-[10px]' : 'text-xs'} mt-1`}>
            {badge.unlocked ? badge.description : badge.requirement}
          </p>
          {badge.unlocked && badge.unlockedAt && (
            <Badge variant="secondary" className="mt-2 text-[10px]">
              Unlocked {new Date(badge.unlockedAt).toLocaleDateString()}
            </Badge>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
